/**
 * Owner Login Page - LOCKED DOWN
 * Elvis Aloha Collection
 * 
 * SECURITY: Owner-only authentication. No registration, no password reset,
 * no public links. Only the canonical owner account can access.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { setPendingOwnerUser } from '../../utils/ownerAuthTransfer';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const OwnerLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const navigate = useNavigate();
  
  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/seat-map/auth/me`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          // Only allow owner_admin role
          if (data.role === 'owner_admin') {
            navigate('/owner-dashboard');
          }
        }
      } catch (err) {
        // Not authenticated, stay on login
      }
    };
    checkAuth();
  }, [navigate]);
  
  // Check lockout status
  useEffect(() => {
    if (lockoutUntil && new Date() < lockoutUntil) {
      const timer = setInterval(() => {
        if (new Date() >= lockoutUntil) {
          setLockoutUntil(null);
          setAttempts(0);
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutUntil]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Check lockout
    if (lockoutUntil && new Date() < lockoutUntil) {
      const remaining = Math.ceil((lockoutUntil - new Date()) / 1000);
      setError(`Too many attempts. Try again in ${remaining} seconds.`);
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      const res = await fetch(`${API_BASE}/api/seat-map/auth/owner-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Store token for other components
        if (data.access_token) {
          localStorage.setItem('ownerToken', data.access_token);
        }
        if (data.user) {
          setPendingOwnerUser(data.user);
        }
        setAttempts(0);
        navigate('/owner-dashboard');
      } else if (res.status === 429) {
        // Rate limited
        const data = await res.json();
        setError(data.detail || 'Too many attempts. Please wait.');
        setLockoutUntil(new Date(Date.now() + 60000)); // 1 minute lockout
      } else if (res.status === 404) {
        // Generic error - don't reveal if account exists
        setError('Access denied');
        setAttempts(prev => prev + 1);
        if (attempts >= 4) {
          setLockoutUntil(new Date(Date.now() + 300000)); // 5 minute lockout
          setError('Too many failed attempts. Please wait 5 minutes.');
        }
      } else {
        setError('Access denied');
        setAttempts(prev => prev + 1);
        if (attempts >= 4) {
          setLockoutUntil(new Date(Date.now() + 300000));
          setError('Too many failed attempts. Please wait 5 minutes.');
        }
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };
  
  const isLockedOut = lockoutUntil && new Date() < lockoutUntil;
  
  return (
    <div 
      data-testid="owner-login-page"
      style={{
        minHeight: '100vh',
        background: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px'
      }}
    >
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div style={{
        background: '#141414',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '380px',
        width: '100%',
        border: '1px solid #222'
      }}>
        {/* Header with Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img 
            src="/icons/logo-white-bg.png" 
            alt="Elvis Aloha Collection"
            style={{ height: '48px', width: 'auto', marginBottom: '16px' }}
          />
          <h1 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 500,
            color: '#666',
            letterSpacing: '0.05em'
          }}>
            Authentication Required
          </h1>
        </div>
        
        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(180, 60, 60, 0.15)',
            border: '1px solid rgba(180, 60, 60, 0.3)',
            borderRadius: '6px',
            color: '#b44',
            fontSize: '13px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}
        
        {/* Login Form */}
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '16px' }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLockedOut}
              autoComplete="username"
              style={{
                width: '100%',
                padding: '14px 16px',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#ccc',
                boxSizing: 'border-box'
              }}
              placeholder="Email"
            />
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLockedOut}
              autoComplete="current-password"
              style={{
                width: '100%',
                padding: '14px 16px',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#ccc',
                boxSizing: 'border-box'
              }}
              placeholder="Password"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading || isLockedOut}
            style={{
              width: '100%',
              padding: '14px',
              background: isLockedOut ? '#333' : '#444',
              color: isLockedOut ? '#666' : '#ccc',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: (loading || isLockedOut) ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s'
            }}
          >
            {loading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>
        
        {/* No links - completely locked down */}
      </div>
    </div>
  );
};

export default OwnerLoginPage;
